import { History as HistoryIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminOrderItem } from "../../../types/adminOrders";
import { getStatusesWithFilters } from "../../../services/orderStatusService";
import { adminOrderItemService } from "../../../services/adminOrderItemService";
import { ItemStatus } from "../../../types/admin";
import { getItemStatusColor } from "../../../utils/statusColors";

interface OrderStatus {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  color?: string;
}

interface OrderStatusUpdateModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  orderNumber: string;
  currentStatus: string;
  currentStatusId?: number;
  orderItems?: AdminOrderItem[];
  onStatusUpdate: (
    orderId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean,
    selectedItemIds?: string[],
    itemStatusId?: number,
  ) => Promise<void>;
}

const OrderStatusUpdateModal: React.FC<OrderStatusUpdateModalProps> = ({
  open,
  onClose,
  orderId,
  orderNumber,
  currentStatus,
  onStatusUpdate,
  orderItems = [],
}) => {
  const { t } = useTranslation();
  const [allStatuses, setAllStatuses] = useState<OrderStatus[]>([]);
  const [itemStatuses, setItemStatuses] = useState<ItemStatus[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | "">("");
  const [selectedItemStatusId, setSelectedItemStatusId] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const [sendNotification, setSendNotification] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedOrderItems, setFetchedOrderItems] = useState<AdminOrderItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Use fetched items if available, otherwise use passed items
  const displayItems = fetchedOrderItems.length > 0 ? fetchedOrderItems : orderItems;

  useEffect(() => {
    if (open) {
      loadOrderStatuses();
      loadItemStatuses();
      loadOrderItems();
      // Reset fetched items when modal opens
      setFetchedOrderItems([]);
    }
  }, [open]);

  const loadOrderStatuses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all order statuses using the service
      const response = await getStatusesWithFilters({
        pageNumber: 1,
        pageSize: 100,
        isActive: true,
        sortBy: "SortOrder",
        sortDescending: false,
      });

      // Extract statuses from the paginated response
      const statuses = response.data?.statuses || [];
      setAllStatuses(statuses);
    } catch (error) {
      setError(
        t("admin.orders.failedToLoadStatuses"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadItemStatuses = async () => {
    try {
      const statuses = await adminOrderItemService.getAvailableItemStatuses();
      setItemStatuses(statuses);
    } catch (error) {
      // Non-critical, don't show error
    }
  };

  const loadOrderItems = async () => {
    // If items are already passed as props or already fetched, don't fetch again
    if (orderItems.length > 0 || fetchedOrderItems.length > 0) {
      return;
    }

    try {
      setLoadingItems(true);
      setError(null);

      // Fetch order items using the correct API endpoint
      const items = await adminOrderItemService.getOrderItemsByOrderId(orderId);

      if (items && items.length > 0) {
        // Map the OrderItemDetail to AdminOrderItem format
        const mappedItems = items.map(item => ({
          id: item.id,
          productId: item.productId || '',
          title: item.title || '',
          price: item.price || 0,
          quantity: item.quantity || 1,
          image: item.image || '',
          sku: item.productId || '',
          subtotal: (item.price || 0) * (item.quantity || 1),
          status: item.itemStatusDisplayName || item.itemStatusName || '',
          statusId: item.itemStatusId,
          notes: item.notes || '',
          retailer: item.retailer || '',
          trackingNumber: item.trackingNumber || '',
          retailerOrderId: item.retailerOrderId || '',
          retailerTrackingId: item.retailerTrackingId || '',
          estimatedDelivery: item.estimatedDelivery || '',
          orderedAt: item.orderedAt || '',
          shippedAt: item.shippedAt || '',
          isShipped: item.hasTrackingNumber || false,
        }));

        setFetchedOrderItems(mappedItems);
      } else {
        setError(t("admin.orders.noItemsFound"));
      }
    } catch (error) {
      setError(t("admin.orders.failedToLoadItems"));
    } finally {
      setLoadingItems(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedStatusId) return;

    try {
      setLoading(true);
      setError(null);

      const itemStatusIdNum = selectedItemStatusId ? Number(selectedItemStatusId) : undefined;

      await onStatusUpdate(
        orderId,
        Number(selectedStatusId),
        notes.trim() || undefined,
        sendNotification,
        sendNotification && selectedItemIds.length > 0 ? selectedItemIds : undefined,
        itemStatusIdNum,
      );

      // Reset form and close modal
      setSelectedStatusId("");
      setSelectedItemStatusId("");
      setNotes("");
      setSendNotification(false);
      setSelectedItemIds([]);
      onClose();
    } catch (error) {
      setError(t("admin.orders.failedToUpdateStatus"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatusId("");
    setSelectedItemStatusId("");
    setNotes("");
    setSendNotification(false);
    setSelectedItemIds([]);
    setError(null);
    onClose();
  };

  const handleItemToggle = (itemId: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAllItems = () => {
    const items = displayItems;
    if (selectedItemIds.length === items.length) {
      setSelectedItemIds([]);
    } else {
      setSelectedItemIds(items.map((item) => item.id));
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    // Try to parse the color from the status, fallback to default colors
    if (status.color && status.color.startsWith("#")) {
      return status.color;
    }

    // Default color mapping based on status name
    const statusName = status.name.toLowerCase();
    if (statusName.includes("pending")) return "#ff9800";
    if (statusName.includes("processing")) return "#2196f3";
    if (statusName.includes("shipped")) return "#9c27b0";
    if (statusName.includes("delivered")) return "#4caf50";
    if (statusName.includes("cancelled")) return "#f44336";
    if (statusName.includes("hold")) return "#ff9800";

    return "#757575";
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon />
          {t("admin.orders.updateOrderStatus")}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Order #{orderNumber}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("admin.orders.currentStatus")}:
            </Typography>
            <Chip
              label={currentStatus}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.orders.newStatus")}</InputLabel>
              <Select
                value={selectedStatusId}
                onChange={(e) => setSelectedStatusId(e.target.value as number)}
                label={t("admin.orders.newStatus")}
                disabled={loading}
              >
                {loading ? (
                  <MenuItem value="" disabled>
                    {t("admin.orders.loadingStatuses")}
                  </MenuItem>
                ) : allStatuses.length > 0 ? (
                  allStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: getStatusColor(status),
                          }}
                        />
                        <Typography>{status.displayName}</Typography>
                        {status.description && (
                          <Typography variant="caption" color="text.secondary">
                            - {status.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    {t("admin.orders.noStatusesAvailable")}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Item Status Dropdown - cascades to all items */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.orders.updateItemStatus")}</InputLabel>
              <Select
                value={selectedItemStatusId}
                onChange={(e) => setSelectedItemStatusId(e.target.value as number)}
                label={t("admin.orders.updateItemStatus")}
                disabled={loading}
              >
                <MenuItem value="">
                  <Typography color="text.secondary">
                    {t("admin.orders.noItemStatusChange")}
                  </Typography>
                </MenuItem>
                {itemStatuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: getItemStatusColor(status),
                        }}
                      />
                      <Typography>{status.displayName}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("admin.orders.notesOptional")}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              placeholder={t("admin.orders.statusNotesPlaceholder")}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                  disabled={loading || loadingItems}
                />
              }
              label={t("admin.orders.sendEmailNotification")}
            />
          </Grid>

          {/* Order Items Section - Always visible for item selection */}
          {sendNotification && (loadingItems ? (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }} color="text.secondary">
                  {t("admin.orders.loadingOrderItems")}
                </Typography>
              </Box>
            </Grid>
          ) : displayItems.length > 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t("admin.orders.selectItemsForEmail")}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleSelectAllItems}
                    disabled={loading}
                  >
                    {selectedItemIds.length === displayItems.length ? t("admin.orders.deselectAll") : t("admin.orders.selectAll")}
                  </Button>
                </Box>

                <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                  {displayItems.map((item) => (
                    <Paper
                      key={item.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#e8e8e8" },
                      }}
                      onClick={() => handleItemToggle(item.id)}
                    >
                      <Checkbox
                        checked={selectedItemIds.includes(item.id)}
                        onChange={() => handleItemToggle(item.id)}
                        disabled={loading}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.title}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.orders.quantity")}: {item.quantity}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Paper>
            </Grid>
          ) : null)}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedStatusId}
        >
          {loading ? t("admin.orders.updating") : t("admin.orders.updateStatus")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderStatusUpdateModal;
